// The kit — everything the printed activation pack needs to know about one
// genie, in one object.
//
// Same shape as the kit registry in HG-CC's
// `scripts/collateral/build-activation-pack.mjs`, so copy tuned here can be
// handed to the terminal builder verbatim and vice versa.
//
// Three layers fill it, in this order:
//
//   saved kit for this genie  >  capability starter kit  >  the genie itself
//
// The genie supplies name, destination and (through its category) which
// capability applies. The capability supplies the words. A saved row overrides
// anything a rep has tuned by hand.

import type { GenieCapability } from "@/types/genie";
import type { PrintBrand } from "@/services/activation/print/printBrand";

/**
 * The genie a pack is printed for, reduced to the four things a printed piece
 * needs. Voice builds this from the activation pack's target rather than from a
 * picker, so there is no source discriminator here — the brand a piece carries
 * is resolved separately, in `printBrand.ts`, and lands on the kit as `brand`.
 */
export interface PackGenieOption {
  /** Stable key, `agent:<uuid>`. Only used to look up per-genie overrides. */
  key: string;
  name: string;
  /** The QR destination. Every code in every document points here. */
  publicUrl: string;
  /** What the genie is for. Drives which starter kit applies. */
  capability: GenieCapability;
}

/** The capability every genie falls back to when nothing else matches. */
export const DEFAULT_PACK_CAPABILITY: GenieCapability = "faq";

/**
 * Keyword to capability, in priority order. A genie carries a name, a category
 * and a description rather than a capability field, so the capability is
 * derived from that text rather than invented as a new column.
 *
 * Mirrors the same table in Help-Genie-Operations
 * (`src/types/activation/activationPack.ts`) so a pack printed from either side
 * resolves to the same starter kit.
 */
const CAPABILITY_KEYWORDS: [GenieCapability, string[]][] = [
  ["emergency-response", ["emergency", "after hours", "after-hours", "urgent", "breakdown", "callout", "on call"]],
  ["order-tracking", ["order", "tracking", "delivery", "dispatch", "shipping", "courier"]],
  ["scheduling", ["booking", "book", "schedule", "scheduling", "appointment", "reception", "calendar", "reservation"]],
  ["quoting", ["quote", "quoting", "estimate", "pricing", "price list"]],
  ["troubleshooting", ["troubleshoot", "troubleshooting", "diagnose", "fault", "repair", "technical", "service desk"]],
  ["concierge", ["concierge", "hospitality", "guest", "front desk", "tourism", "hotel"]],
  ["lead-qualification", ["lead capture", "lead", "qualify", "qualification", "sales", "enquiry", "enquiries", "inquiry"]],
  ["follow-up", ["follow up", "follow-up", "nurture", "reminder", "re-engage", "win back"]],
  ["product-advisor", ["product", "advisor", "recommend", "retail", "catalogue", "catalog", "shop"]],
  ["intake", ["intake", "onboarding", "onboard", "form", "new client", "new customer", "setup", "registration"]],
  ["faq", ["faq", "support", "help", "explainer", "knowledge base", "helpdesk", "questions", "internal support", "training"]],
];

/**
 * Best-effort capability from whatever descriptive text a genie carries (name,
 * category, description). Returns the default rather than throwing, so a genie
 * always lands on a real starter kit and never prints a blank sheet.
 */
export const inferPackCapability = (terms: (string | undefined | null)[]): GenieCapability => {
  const hay = terms.filter(Boolean).join(" ").toLowerCase();
  if (!hay.trim()) return DEFAULT_PACK_CAPABILITY;
  for (const [capability, keywords] of CAPABILITY_KEYWORDS) {
    if (keywords.some((word) => hay.includes(word))) return capability;
  }
  return DEFAULT_PACK_CAPABILITY;
};

/** One row of the "where to put it" table: a place, and what goes there. */
export type PackPlacement = [place: string, what: string];

export interface PackKit {
  /** Filename stem and preset key. Derived from the genie name. */
  slug: string;
  /** Full business name. Appears on stickers, cards and the cover. */
  name: string;
  /** Short form for tight spaces (small stickers, the table tent). */
  shortName: string;
  /** The QR destination. Every code in the pack points here. */
  url: string;
  /** The line under the poster and on the back of a card: a domain or the name. */
  footer: string;
  /** What the genie is for, in the audience's words. Drives poster copy. */
  subject: string;
  /** The prompt line on a sticker. */
  stickerLine: string;
  /**
   * Four things the genie helps with. Categories, never example questions.
   *
   * REVISED 2026-08-06 off Tim's activation feedback. This was `questions`, four
   * things a real person would ask, printed as `Try: "..."` under a poster. A
   * printed example narrows the reader down to that one thing and misfires when
   * what they actually wanted was something else. Categories say the shape of
   * what is on offer and let the reader bring their own question.
   */
  helps: string[];
  /** The genie's real name in the platform, normalised. Never edited by hand. */
  genieName: string;
  /** The use case alone, for formats too small to carry the business name too. */
  genieUseCase: string;
  /** A round-sticker-length form of the name. Shortened by tail, never cut. */
  stickerName: string;
  /** True when there is no business name distinct from the genie's own name. */
  nameIsGenie: boolean;
  /** The second line under the name: the business, or a stand-in when it would repeat. */
  subLine: string;
  /** The footer line, or null when it would only repeat the name above it. */
  footerLine: string | null;
  /** Who is scanning. Appears in poster copy. */
  audience: string;
  /** Where the material goes once it is printed. Prints on the contents page. */
  placements: PackPlacement[];
  /**
   * Whose brand the printed piece carries — the customer's, derived from the
   * genie's branding. Every page builder already takes a kit, which is why the
   * brand rides along on it rather than being threaded through nine signatures.
   */
  brand: PrintBrand;
}

/**
 * The parts of a kit a rep edits. Only the URL and the slug come from the genie
 * and stay fixed: a genie is often called "Gladstone Tennis Club Genie" while
 * the sticker on the gate has to say "Gladstone Tennis Club".
 */
export type PackKitCopy = Pick<
  PackKit,
  | "name"
  | "shortName"
  | "footer"
  | "subject"
  | "stickerLine"
  // ADDED 2026-08-09. `helps` used to be deliberately excluded, on the grounds
  // that "covers follow the capability, so there is nothing for a rep to tune,
  // nothing to persist, and no migration". Those four lines are the largest
  // words on the default poster, and for most genies they resolved to the `faq`
  // fallback — so a print software company's poster offered "helpful tips and
  // guides". They are the first thing anybody wants to change.
  | "helps"
  | "audience"
  | "placements"
>;

/** Placements that suit almost any business. Overridden per capability below. */
const DEFAULT_PLACEMENTS: PackPlacement[] = [
  ["Front door", "A4 poster at eye height on the inside of the glass"],
  ["Counter or reception", "Table tent, plus a stack of pocket cards"],
  ["Vehicles and equipment", "Round stickers, 60mm, on a flat clean surface"],
  ["Noticeboard", "The big QR poster, so it reads from across the room"],
  ["Email and newsletter", "Photograph a card and drop it into the next send"],
];

/** The words a capability can supply. The business name never comes from here. */
export type CapabilityKitCopy = Pick<
  PackKit,
  "subject" | "stickerLine" | "helps" | "audience" | "placements"
>;

/**
 * A starter kit per capability, so a genie that has never been touched still
 * produces a pack worth printing. Exhaustive by type: adding a capability to
 * `GenieCapability` without copy here is a compile error.
 */
export const CAPABILITY_KITS: Record<GenieCapability, CapabilityKitCopy> = {
  "emergency-response": {
    subject: "urgent callouts, faults and after hours help",
    stickerLine: "Scan for urgent help",
    helps: [
      "Urgent call outs, any hour",
      "What to do before we arrive",
      "Job updates while you wait",
      "Multi language conversations",
    ],
    audience: "customers and site staff",
    placements: [
      ["Switchboard or meter box", "Square sticker, 75mm, where a fault gets found"],
      ["Plant and machinery", "Round stickers, 60mm, on the control panel"],
      ["Site entrance", "A4 poster, laminated, on the gate"],
      ["Vehicles", "Window cling on the rear glass"],
      ["Handover pack", "A pocket card stapled to the paperwork"],
    ],
  },
  scheduling: {
    subject: "bookings, availability and appointment times",
    stickerLine: "Scan to book",
    helps: [
      "Live availability and booking",
      "Rescheduling and cancellations",
      "What to expect on the day",
      "Multi language conversations",
    ],
    audience: "customers and visitors",
    placements: [
      ["Reception desk", "Table tent, right where people ask about times"],
      ["Front door", "A4 poster at eye height"],
      ["Waiting area", "The big QR poster on the wall"],
      ["Counter", "A stack of pocket cards to hand out on the way out"],
      ["Confirmation email", "Photograph a card and add it to the footer"],
    ],
  },
  quoting: {
    subject: "prices, quotes and what a job costs",
    stickerLine: "Scan for a price",
    helps: [
      "Pricing and what is included",
      "A quote for your job on the spot",
      "Lead times and availability",
      "Multi language conversations",
    ],
    audience: "customers and prospects",
    placements: [
      ["Showroom or yard", "A4 poster beside the product people ask about"],
      ["Vehicles", "Round stickers, 60mm, on both doors"],
      ["Site signage", "Window cling on the site board"],
      ["Counter", "Table tent plus a stack of cards"],
      ["Quote paperwork", "A pocket card in every envelope"],
    ],
  },
  troubleshooting: {
    subject: "faults, fixes and how things work",
    stickerLine: "Scan if it is not working",
    helps: [
      "Technical product support",
      "Step by step fault fixes",
      "When to book a service call",
      "Multi language conversations",
    ],
    audience: "customers and staff",
    placements: [
      ["On the equipment", "Label sticker, 90 x 35mm, on the panel"],
      ["Machine or appliance", "Round sticker, 60mm, beside the controls"],
      ["Staff room", "A4 poster where the team can see it"],
      ["Service manual", "A pocket card taped inside the cover"],
      ["Storeroom shelf", "Counter strips along the shelf lip"],
    ],
  },
  concierge: {
    subject: "local tips, requests and anything you need",
    stickerLine: "Scan to ask us",
    helps: [
      "Local tips and recommendations",
      "Requests and bookings",
      "Everything on site and nearby",
      "Multi language conversations",
    ],
    audience: "guests and visitors",
    placements: [
      ["Guest rooms", "Table tent on the desk in every room"],
      ["Reception", "A4 poster behind the desk"],
      ["Lift lobby and corridors", "Square stickers, 75mm, at eye height"],
      ["Bar and cafe", "Counter strips along the counter edge"],
      ["Welcome pack", "A pocket card in every folder"],
    ],
  },
  "lead-qualification": {
    subject: "what we do and who to talk to",
    stickerLine: "Scan to get started",
    helps: [
      "What we do and who we do it for",
      "Pricing and what is included",
      "Getting you to the right person",
      "Multi language conversations",
    ],
    audience: "prospects and visitors",
    placements: [
      ["Trade stand or event table", "Table tent plus a stack of pocket cards"],
      ["Vehicles", "Round stickers, 60mm, on both doors"],
      ["Site and yard signage", "Window cling on the board"],
      ["Front door", "A4 poster at eye height"],
      ["Business card stack", "Cut from the cards page and carry them"],
    ],
  },
  "follow-up": {
    subject: "your job, your quote and where things are up to",
    stickerLine: "Scan to pick up where we left off",
    helps: [
      "Where your enquiry got to",
      "Pricing and what is included",
      "Booking the next step",
      "Multi language conversations",
    ],
    audience: "customers and prospects",
    placements: [
      ["Quote and invoice paperwork", "A pocket card in every envelope"],
      ["Counter", "Table tent where people settle up"],
      ["Front door", "A4 poster at eye height"],
      ["Vehicles", "Round stickers, 60mm"],
      ["Follow up email", "Photograph a card and drop it in"],
    ],
  },
  faq: {
    subject: "anything you need to know",
    stickerLine: "Scan to ask us",
    helps: [
      "Technical product support",
      "Helpful tips and guides",
      "Hours, pricing and policies",
      "Multi language conversations",
    ],
    audience: "customers and visitors",
    placements: DEFAULT_PLACEMENTS,
  },
  "product-advisor": {
    subject: "which product suits you and what the difference is",
    stickerLine: "Scan for a recommendation",
    helps: [
      "Expert advice on our product range",
      "Comparing options side by side",
      "Availability and lead times",
      "Multi language conversations",
    ],
    audience: "shoppers and visitors",
    placements: [
      ["Shelf edges", "Label stickers, 90 x 35mm, under the product"],
      ["Product displays", "Counter strips along the shelf lip"],
      ["Fitting room or aisle end", "A4 poster at eye height"],
      ["Till", "Table tent plus a stack of cards"],
      ["Window", "The 150mm cling on the glass"],
    ],
  },
  "order-tracking": {
    subject: "orders, deliveries and where your parcel is",
    stickerLine: "Scan to track your order",
    helps: [
      "Live order and delivery status",
      "Changing or cancelling an order",
      "Returns and replacements",
      "Multi language conversations",
    ],
    audience: "customers",
    placements: [
      ["Parcels and cartons", "Label stickers, 90 x 35mm, on the box"],
      ["Despatch bench", "A4 poster where packing happens"],
      ["Delivery vehicles", "Round stickers, 60mm, on the rear door"],
      ["Trade counter", "Table tent plus a stack of cards"],
      ["Packing slip", "A pocket card in every carton"],
    ],
  },
  intake: {
    subject: "getting set up with us and what we need from you",
    stickerLine: "Scan to get set up",
    helps: [
      "Getting set up with us",
      "What we need from you",
      "What happens next, and when",
      "Multi language conversations",
    ],
    audience: "new customers",
    placements: [
      ["Reception", "A4 poster at eye height beside the desk"],
      ["Waiting area", "Table tent on every table"],
      ["Front door", "Window cling on the glass"],
      ["Welcome pack", "A pocket card in every folder"],
      ["Confirmation email", "Photograph a card and add it to the footer"],
    ],
  },
};

/**
 * "<Business> <Use case> Genie" — the genie's real name, normalised.
 *
 * Tim's third rule, same 2026-08-06 feedback: collateral refers to the genie by
 * its name so the use case a customer picked in the platform carries through
 * onto the sticker without anyone re-explaining it. Customers name genies
 * inconsistently — some already include the company, some already end in
 * "Genie", some are a bare use case — so this normalises rather than printing
 * "Giltrap Agrizone Giltrap Agrizone Technical Support Genie Genie".
 *
 * Mirrors `genieDisplayName` in HG-CC's scripts/activation/executors/qr-pack.mjs.
 */
export const genieDisplayName = (company: string, genieName?: string | null): string => {
  const raw = (genieName ?? "").trim();
  // "<Company> Genie", not "<Company> Help Genie". The material is the
  // customer's, and the thing being introduced on it is their Genie — Help
  // Genie is the company that makes it, which the foot of the sheet says.
  if (!raw) return `${company} Genie`;
  let out = raw;
  if (!/genie$/i.test(out)) out = `${out} Genie`;
  if (!out.toLowerCase().includes(company.toLowerCase())) out = `${company} ${out}`;
  return out;
};

/** The use case on its own. A 60mm round cannot hold both halves of the name. */
export const genieUseCaseOf = (company: string, genieName?: string | null): string => {
  const full = genieDisplayName(company, genieName);
  const stripped = full.toLowerCase().startsWith(company.toLowerCase())
    ? full.slice(company.length).trim()
    : full;
  // Never strip down to nothing. When a rep has typed no business name the
  // kit's `name` IS the genie name, and the remainder is an empty string —
  // which would print a blank line on every sticker.
  return stripped || full;
};

/**
 * "Introducing the ___".
 *
 * Anything already ending in "Genie" stands on its own — most often a kit whose
 * name IS the genie's name because no business name was typed, which would
 * otherwise read "Introducing the Alpers Dental Staff Policy Genie Genie".
 *
 * REVISED 2026-08-09 from `${company} Help Genie`. On a poster going up in a
 * customer's reception, "Introducing the PrintIQ Help Genie" introduces the
 * wrong company. Prefer the genie's own resolved name where there is one; this
 * is the fallback for a kit that only knows a business name.
 */
export const heroTitleOf = (company: string): string =>
  /genie$/i.test(company) ? company : `${company} Genie`;

/**
 * A round sticker holds about 24 characters at a readable size. Shorten by
 * dropping leading words rather than truncating, so the tail — the use case and
 * the word "Genie" — always survives:
 *
 *   "Alpers Dental Staff Policy Genie"     -> "Staff Policy Genie"
 *   "Maintenance & Troubleshooting Genie"  -> "Troubleshooting Genie"
 *
 * Truncating instead would give "Alpers Dental Staff Po…", which names nothing.
 */
export const shortenGenieName = (value: string, limit = 24): string => {
  let words = value.trim().split(/\s+/);
  while (words.length > 1 && words.join(" ").length > limit) words = words.slice(1);
  while (words.length > 1 && /^[&+\-–—/]+$/.test(words[0])) words = words.slice(1);
  return words.join(" ");
};

const same = (a?: string | null, b?: string | null): boolean =>
  (a ?? "").trim().toLowerCase() === (b ?? "").trim().toLowerCase();

const slugify = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "") || "genie";

/** A short form that still reads as the business. "Gladstone Tennis Club" -> "Gladstone Tennis". */
const shortenName = (name: string): string => {
  const trimmed = name.trim();
  if (trimmed.length <= 22) return trimmed;
  const words = trimmed.split(/\s+/);
  return words.length > 2 ? words.slice(0, 2).join(" ") : trimmed;
};

/**
 * The genie plus its capability starter kit, with any saved copy on top.
 *
 * Nothing else in the app decides this precedence. The initial fill and the
 * reset affordance both read from here.
 */
export const resolvePackKit = (
  genie: PackGenieOption | undefined,
  saved: Partial<PackKitCopy> | null | undefined,
  brand: PrintBrand,
): PackKit => {
  const capability = genie?.capability ?? "faq";
  const preset = CAPABILITY_KITS[capability] ?? CAPABILITY_KITS.faq;
  const name = saved?.name?.trim() || genie?.name?.trim() || "Your business";
  const savedPlacements = saved?.placements?.filter((p) => p?.[0]?.trim());
  // Four lines, always. A poster laid out for four categories printing three
  // leaves a hole in the card stack, and printing five pushes the code off the
  // sheet — so short lists fall back to the preset rather than being padded.
  const savedHelps = saved?.helps?.map((h) => h?.trim()).filter(Boolean);
  const genieName = genieDisplayName(name, genie?.name);
  const genieUseCase = genieUseCaseOf(name, genie?.name);
  const nameIsGenie = same(name, genieName) || same(name, genieUseCase);
  const footer = saved?.footer?.trim() || name;

  return {
    slug: slugify(name),
    name,
    shortName: saved?.shortName?.trim() || shortenName(name),
    url: genie?.publicUrl?.trim() || "",
    footer,
    subject: saved?.subject?.trim() || preset.subject,
    stickerLine: saved?.stickerLine?.trim() || preset.stickerLine,
    helps: savedHelps?.length === 4 ? savedHelps : preset.helps,
    genieName,
    genieUseCase,
    // A rep who has typed no business name leaves `name` equal to the genie
    // name, and every piece would then print it two or three times over. These
    // are the deduped secondary lines each piece uses instead.
    stickerName: genieName.length <= 24 ? genieName : shortenGenieName(genieUseCase),
    nameIsGenie,
    subLine: nameIsGenie ? "Answers, any hour" : name,
    footerLine: footer && !same(footer, name) && !same(footer, genieName) ? footer : null,
    audience: saved?.audience?.trim() || preset.audience,
    placements: savedPlacements?.length ? savedPlacements : preset.placements,
    brand,
  };
};

/** The kit as the capability alone would fill it. What "reset" restores. */
export const presetKitFor = (genie: PackGenieOption | undefined, brand: PrintBrand): PackKit =>
  resolvePackKit(genie, null, brand);
